import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabaseClient";
import {
  AktualitaItem,
  BalicekContent,
  BistroNwtContent,
  CenikRadek,
  DenniNabidkaContent,
  deleteAktualita,
  fetchAktuality,
  fetchBalicek,
  fetchBistroNwt,
  fetchDenniNabidka,
  saveBalicek,
  saveBistroNwt,
  saveDenniNabidka,
  uploadCmsAsset,
  upsertAktualita,
} from "../../lib/cms.ts";

type AdminTab = "aktuality" | "denniNabidka" | "bistroNwt" | "balicky";

const initialAktualita: Omit<AktualitaItem, "id"> = {
  nadpis: "",
  text: "",
  datum: "",
  obrazekUrl: null,
};

const initialBalicek: BalicekContent = {
  id: 1,
  nadpis: "",
  podnadpis: "",
  uvodniText: "",
  obsahBalicku: [],
  cenik: [],
  dodatek: "",
  obrazekMobilniUrl: null,
  obrazekDesktop1Url: null,
  obrazekDesktop2Url: null,
};

const panelStyle: React.CSSProperties = {
  background: "#fff",
  borderRadius: 16,
  padding: 24,
  boxShadow: "0 16px 40px rgba(0,0,0,0.08)",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  border: "1px solid #d7d2cb",
  borderRadius: 10,
  padding: "12px 14px",
  fontSize: 14,
  boxSizing: "border-box",
};

const buttonStyle: React.CSSProperties = {
  border: "none",
  borderRadius: 10,
  padding: "12px 16px",
  fontSize: 14,
  cursor: "pointer",
};

const secondaryButtonStyle: React.CSSProperties = {
  ...buttonStyle,
  background: "#f2ece6",
  color: "#4c2e1f",
};

const primaryButtonStyle: React.CSSProperties = {
  ...buttonStyle,
  background: "#5a2914",
  color: "#fff",
};

function Admin() {
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [tab, setTab] = useState<AdminTab>("aktuality");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");

  const [aktuality, setAktuality] = useState<AktualitaItem[]>([]);
  const [aktualitaForm, setAktualitaForm] = useState(initialAktualita);
  const [editingAktualitaId, setEditingAktualitaId] = useState<string | null>(null);
  const [denniNabidka, setDenniNabidka] = useState<DenniNabidkaContent>({ id: 1, obrazekUrl: null });
  const [bistroNwt, setBistroNwt] = useState<BistroNwtContent>({
    id: 1,
    textRablinu: "",
    obrazekRablinuUrl: null,
    pdfRablinuUrl: null,
    textNWT: "",
    obrazekNWTUrl: null,
    pdfNWTUrl: null,
  });
  const [balicek, setBalicek] = useState<BalicekContent>(initialBalicek);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session) return;
    loadCmsData();
  }, [session]);

  async function loadCmsData() {
    setLoading(true);
    setNotice("");

    try {
      const [aktualityData, denniNabidkaData, bistroNwtData, balicekData] =
        await Promise.all([
          fetchAktuality(),
          fetchDenniNabidka(),
          fetchBistroNwt(),
          fetchBalicek(),
        ]);

      setAktuality(aktualityData);
      setDenniNabidka(denniNabidkaData);
      setBistroNwt(bistroNwtData);
      setBalicek(balicekData);
    } catch (error: any) {
      setNotice(error.message ?? "Nepodařilo se načíst CMS data.");
    } finally {
      setLoading(false);
    }
  }

  async function handleLogin(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setAuthError("");

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      setAuthError(error.message);
    }

    setBusy(false);
  }

  async function handleLogout() {
    setBusy(true);
    await supabase.auth.signOut();
    setBusy(false);
  }

  async function uploadAndSetUrl(
    file: File | null,
    folder: string,
    setter: (value: string) => void
  ) {
    if (!file) return;
    setBusy(true);
    setNotice("");

    try {
      const publicUrl = await uploadCmsAsset(file, folder);
      setter(publicUrl);
      setNotice("Soubor byl nahrán do Supabase Storage.");
    } catch (error: any) {
      setNotice(error.message ?? "Soubor se nepodařilo nahrát.");
    } finally {
      setBusy(false);
    }
  }

  async function handleSaveAktualita(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setNotice("");

    try {
      const saved = await upsertAktualita({
        id: editingAktualitaId ?? undefined,
        ...aktualitaForm,
      });

      setAktuality((current) => {
        const withoutEdited = current.filter((item) => item.id !== saved.id);
        return [saved, ...withoutEdited];
      });

      setAktualitaForm(initialAktualita);
      setEditingAktualitaId(null);
      setNotice("Aktualita byla uložena.");
    } catch (error: any) {
      setNotice(error.message ?? "Aktualitu se nepodařilo uložit.");
    } finally {
      setBusy(false);
    }
  }

  async function handleDeleteAktualita(id: string) {
    setBusy(true);
    setNotice("");

    try {
      await deleteAktualita(id);
      setAktuality((current) => current.filter((item) => item.id !== id));
      if (editingAktualitaId === id) {
        setAktualitaForm(initialAktualita);
        setEditingAktualitaId(null);
      }
      setNotice("Aktualita byla smazána.");
    } catch (error: any) {
      setNotice(error.message ?? "Aktualitu se nepodařilo smazat.");
    } finally {
      setBusy(false);
    }
  }

  async function handleSaveDenniNabidka() {
    setBusy(true);
    setNotice("");

    try {
      await saveDenniNabidka(denniNabidka);
      setNotice("Denní nabídka byla uložena.");
    } catch (error: any) {
      setNotice(error.message ?? "Denní nabídku se nepodařilo uložit.");
    } finally {
      setBusy(false);
    }
  }

  async function handleSaveBistroNwt() {
    setBusy(true);
    setNotice("");

    try {
      await saveBistroNwt(bistroNwt);
      setNotice("Obsah denní nabídky pro Hospůdku pod Rablinů byl uložen.");
    } catch (error: any) {
      setNotice(error.message ?? "Obsah denní nabídky pro Hospůdku pod Rablinů se nepodařilo uložit.");
    } finally {
      setBusy(false);
    }
  }

  async function handleSaveBalicek() {
    setBusy(true);
    setNotice("");

    try {
      await saveBalicek(balicek);
      setNotice("Balíček byl uložen.");
    } catch (error: any) {
      setNotice(error.message ?? "Balíček se nepodařilo uložit.");
    } finally {
      setBusy(false);
    }
  }

  const obsahBalickuText = useMemo(
    () => balicek.obsahBalicku.join("\n"),
    [balicek.obsahBalicku]
  );

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "#f7f1eb" }}>
        Načítám CMS…
      </div>
    );
  }

  if (!session) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          padding: 24,
          background: "linear-gradient(180deg, #f5ede7 0%, #fff 100%)",
        }}
      >
        <form onSubmit={handleLogin} style={{ ...panelStyle, width: "100%", maxWidth: 420 }}>
          <h1 style={{ marginTop: 0, marginBottom: 8, color: "#5a2914" }}>CMS administrace</h1>
          <p style={{ marginTop: 0, color: "#6f5a4b", lineHeight: 1.5 }}>
            Přihlášení probíhá přes Supabase Auth. Použij účet, který vytvoříš v Supabase.
          </p>

          <div style={{ display: "grid", gap: 14 }}>
            <input
              type="email"
              placeholder="E-mail"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              style={inputStyle}
            />
            <input
              type="password"
              placeholder="Heslo"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              style={inputStyle}
            />
            {authError ? <div style={{ color: "#a12727" }}>{authError}</div> : null}
            <button type="submit" disabled={busy} style={primaryButtonStyle}>
              {busy ? "Přihlašuji…" : "Přihlásit se"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f7f1eb", padding: 24, boxSizing: "border-box" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gap: 24 }}>
        <header style={{ ...panelStyle, display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
          <div>
            <h1 style={{ margin: 0, color: "#5a2914" }}>Vlastní CMS</h1>
            <p style={{ marginBottom: 0, color: "#6f5a4b" }}>
              Správa obsahu pro veřejný web bez Sanity.
            </p>
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ color: "#6f5a4b" }}>{session.user?.email}</span>
            <button type="button" onClick={handleLogout} disabled={busy} style={secondaryButtonStyle}>
              Odhlásit se
            </button>
          </div>
        </header>

        <nav style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {[
            ["aktuality", "Aktuality"],
            ["denniNabidka", "Denní nabídka"],
            ["bistroNwt", "Nabídka Rablinů"],
            ["balicky", "Balíčky"],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key as AdminTab)}
              style={{
                ...buttonStyle,
                background: tab === key ? "#5a2914" : "#fff",
                color: tab === key ? "#fff" : "#5a2914",
                boxShadow: "0 10px 24px rgba(0,0,0,0.06)",
              }}
            >
              {label}
            </button>
          ))}
        </nav>

        {notice ? (
          <div style={{ ...panelStyle, padding: 16, color: "#4c2e1f" }}>{notice}</div>
        ) : null}

        {tab === "aktuality" ? (
          <div style={{ display: "grid", gap: 24 }}>
            <form onSubmit={handleSaveAktualita} style={panelStyle}>
              <h2 style={{ marginTop: 0 }}>Aktualita</h2>
              <div style={{ display: "grid", gap: 14 }}>
                <input
                  value={aktualitaForm.nadpis}
                  onChange={(event) => setAktualitaForm((current) => ({ ...current, nadpis: event.target.value }))}
                  placeholder="Nadpis"
                  style={inputStyle}
                />
                <textarea
                  value={aktualitaForm.text}
                  onChange={(event) => setAktualitaForm((current) => ({ ...current, text: event.target.value }))}
                  placeholder="Text aktuality"
                  rows={6}
                  style={inputStyle}
                />
                <input
                  value={aktualitaForm.datum ?? ""}
                  onChange={(event) => setAktualitaForm((current) => ({ ...current, datum: event.target.value }))}
                  placeholder="Datum akce"
                  style={inputStyle}
                />
                <div style={{ display: "grid", gap: 8 }}>
                  <input
                    value={aktualitaForm.obrazekUrl ?? ""}
                    onChange={(event) => setAktualitaForm((current) => ({ ...current, obrazekUrl: event.target.value }))}
                    placeholder="URL obrázku"
                    style={inputStyle}
                  />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(event) =>
                      uploadAndSetUrl(
                        event.target.files?.[0] ?? null,
                        "aktuality",
                        (value) =>
                          setAktualitaForm((current) => ({ ...current, obrazekUrl: value }))
                      )
                    }
                  />
                </div>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <button type="submit" disabled={busy} style={primaryButtonStyle}>
                    {editingAktualitaId ? "Uložit změny" : "Vytvořit aktualitu"}
                  </button>
                  {editingAktualitaId ? (
                    <button
                      type="button"
                      disabled={busy}
                      style={secondaryButtonStyle}
                      onClick={() => {
                        setEditingAktualitaId(null);
                        setAktualitaForm(initialAktualita);
                      }}
                    >
                      Zrušit editaci
                    </button>
                  ) : null}
                </div>
              </div>
            </form>

            <div style={{ ...panelStyle, display: "grid", gap: 16 }}>
              <h2 style={{ marginTop: 0 }}>Seznam aktualit</h2>
              {aktuality.length === 0 ? <div>Zatím nejsou uložené žádné aktuality.</div> : null}
              {aktuality.map((item) => (
                <article
                  key={item.id}
                  style={{
                    border: "1px solid #ede3d9",
                    borderRadius: 12,
                    padding: 16,
                    display: "grid",
                    gap: 10,
                  }}
                >
                  <strong>{item.nadpis}</strong>
                  <div style={{ color: "#6f5a4b" }}>{item.datum ?? "bez data"}</div>
                  <div style={{ whiteSpace: "pre-wrap" }}>{item.text}</div>
                  {item.obrazekUrl ? (
                    <a href={item.obrazekUrl} target="_blank" rel="noreferrer">
                      Otevřít obrázek
                    </a>
                  ) : null}
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      style={secondaryButtonStyle}
                      onClick={() => {
                        setEditingAktualitaId(item.id);
                        setAktualitaForm({
                          nadpis: item.nadpis,
                          text: item.text,
                          datum: item.datum ?? "",
                          obrazekUrl: item.obrazekUrl,
                        });
                      }}
                    >
                      Upravit
                    </button>
                    <button
                      type="button"
                      style={{ ...secondaryButtonStyle, background: "#fde8e8", color: "#912626" }}
                      onClick={() => handleDeleteAktualita(item.id)}
                    >
                      Smazat
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        ) : null}

        {tab === "denniNabidka" ? (
          <section style={panelStyle}>
            <h2 style={{ marginTop: 0 }}>Denní nabídka</h2>
            <div style={{ display: "grid", gap: 14 }}>
              <input
                value={denniNabidka.obrazekUrl ?? ""}
                onChange={(event) =>
                  setDenniNabidka((current) => ({ ...current, obrazekUrl: event.target.value }))
                }
                placeholder="URL obrázku"
                style={inputStyle}
              />
              <input
                type="file"
                accept="image/*"
                onChange={(event) =>
                  uploadAndSetUrl(
                    event.target.files?.[0] ?? null,
                    "denni-nabidka",
                    (value) => setDenniNabidka((current) => ({ ...current, obrazekUrl: value }))
                  )
                }
              />
              <button type="button" disabled={busy} style={primaryButtonStyle} onClick={handleSaveDenniNabidka}>
                Uložit denní nabídku
              </button>
            </div>
          </section>
        ) : null}

        {tab === "bistroNwt" ? (
          <section style={{ ...panelStyle, display: "grid", gap: 24 }}>
            <h2 style={{ marginTop: 0 }}>Denní nabídka – Hospůdka pod Rablinů</h2>

            <div style={{ display: "grid", gap: 14 }}>
              <h3 style={{ marginBottom: 0 }}>Hospůdka pod Rablinů</h3>
              <textarea
                value={bistroNwt.textRablinu}
                onChange={(event) => setBistroNwt((current) => ({ ...current, textRablinu: event.target.value }))}
                placeholder="Text pro Rablinů"
                rows={4}
                style={inputStyle}
              />
              <input
                value={bistroNwt.obrazekRablinuUrl ?? ""}
                onChange={(event) =>
                  setBistroNwt((current) => ({ ...current, obrazekRablinuUrl: event.target.value }))
                }
                placeholder="URL obrázku pro Rablinů"
                style={inputStyle}
              />
              <input
                type="file"
                accept="image/*"
                onChange={(event) =>
                  uploadAndSetUrl(
                    event.target.files?.[0] ?? null,
                    "bistro-nwt",
                    (value) =>
                      setBistroNwt((current) => ({ ...current, obrazekRablinuUrl: value }))
                  )
                }
              />
              <input
                value={bistroNwt.pdfRablinuUrl ?? ""}
                onChange={(event) =>
                  setBistroNwt((current) => ({ ...current, pdfRablinuUrl: event.target.value }))
                }
                placeholder="URL PDF pro Rablinů"
                style={inputStyle}
              />
              <input
                type="file"
                accept="application/pdf"
                onChange={(event) =>
                  uploadAndSetUrl(
                    event.target.files?.[0] ?? null,
                    "bistro-nwt",
                    (value) =>
                      setBistroNwt((current) => ({ ...current, pdfRablinuUrl: value }))
                  )
                }
              />
            </div>

            <button type="button" disabled={busy} style={primaryButtonStyle} onClick={handleSaveBistroNwt}>
              Uložit nabídku
            </button>
          </section>
        ) : null}

        {tab === "balicky" ? (
          <section style={{ ...panelStyle, display: "grid", gap: 24 }}>
            <h2 style={{ marginTop: 0 }}>Balíčky</h2>
            <input
              value={balicek.nadpis}
              onChange={(event) => setBalicek((current) => ({ ...current, nadpis: event.target.value }))}
              placeholder="Nadpis"
              style={inputStyle}
            />
            <input
              value={balicek.podnadpis}
              onChange={(event) => setBalicek((current) => ({ ...current, podnadpis: event.target.value }))}
              placeholder="Podnadpis"
              style={inputStyle}
            />
            <textarea
              value={balicek.uvodniText}
              onChange={(event) => setBalicek((current) => ({ ...current, uvodniText: event.target.value }))}
              placeholder="Úvodní text"
              rows={4}
              style={inputStyle}
            />
            <textarea
              value={obsahBalickuText}
              onChange={(event) =>
                setBalicek((current) => ({
                  ...current,
                  obsahBalicku: event.target.value
                    .split("\n")
                    .map((item) => item.trim())
                    .filter(Boolean),
                }))
              }
              placeholder="Obsah balíčku, každý řádek jedna položka"
              rows={6}
              style={inputStyle}
            />

            <div style={{ display: "grid", gap: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <h3 style={{ margin: 0 }}>Ceník</h3>
                <button
                  type="button"
                  style={secondaryButtonStyle}
                  onClick={() =>
                    setBalicek((current) => ({
                      ...current,
                      cenik: [...current.cenik, { popis: "", noci2: "", noci3: "", noci4: "" }],
                    }))
                  }
                >
                  Přidat řádek
                </button>
              </div>

              {balicek.cenik.map((radek: CenikRadek, index: number) => (
                <div
                  key={`${index}-${radek.popis}`}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "2fr 1fr 1fr 1fr auto",
                    gap: 10,
                    alignItems: "center",
                  }}
                >
                  <input
                    value={radek.popis}
                    onChange={(event) =>
                      setBalicek((current) => ({
                        ...current,
                        cenik: current.cenik.map((item, itemIndex) =>
                          itemIndex === index ? { ...item, popis: event.target.value } : item
                        ),
                      }))
                    }
                    placeholder="Popis"
                    style={inputStyle}
                  />
                  <input
                    value={radek.noci2}
                    onChange={(event) =>
                      setBalicek((current) => ({
                        ...current,
                        cenik: current.cenik.map((item, itemIndex) =>
                          itemIndex === index ? { ...item, noci2: event.target.value } : item
                        ),
                      }))
                    }
                    placeholder="2 noci"
                    style={inputStyle}
                  />
                  <input
                    value={radek.noci3}
                    onChange={(event) =>
                      setBalicek((current) => ({
                        ...current,
                        cenik: current.cenik.map((item, itemIndex) =>
                          itemIndex === index ? { ...item, noci3: event.target.value } : item
                        ),
                      }))
                    }
                    placeholder="3 noci"
                    style={inputStyle}
                  />
                  <input
                    value={radek.noci4}
                    onChange={(event) =>
                      setBalicek((current) => ({
                        ...current,
                        cenik: current.cenik.map((item, itemIndex) =>
                          itemIndex === index ? { ...item, noci4: event.target.value } : item
                        ),
                      }))
                    }
                    placeholder="4 noci"
                    style={inputStyle}
                  />
                  <button
                    type="button"
                    style={{ ...secondaryButtonStyle, background: "#fde8e8", color: "#912626" }}
                    onClick={() =>
                      setBalicek((current) => ({
                        ...current,
                        cenik: current.cenik.filter((_, itemIndex) => itemIndex !== index),
                      }))
                    }
                  >
                    Smazat
                  </button>
                </div>
              ))}
            </div>

            <textarea
              value={balicek.dodatek}
              onChange={(event) => setBalicek((current) => ({ ...current, dodatek: event.target.value }))}
              placeholder="Text pod tabulkou"
              rows={3}
              style={inputStyle}
            />

            {[
              ["obrazekMobilniUrl", "Obrázek mobil", "balicky"],
              ["obrazekDesktop1Url", "Obrázek desktop 1", "balicky"],
              ["obrazekDesktop2Url", "Obrázek desktop 2", "balicky"],
            ].map(([field, label, folder]) => (
              <div key={field} style={{ display: "grid", gap: 8 }}>
                <label>{label}</label>
                <input
                  value={(balicek as any)[field] ?? ""}
                  onChange={(event) =>
                    setBalicek((current) => ({ ...current, [field]: event.target.value }))
                  }
                  placeholder={`URL pro ${label.toLowerCase()}`}
                  style={inputStyle}
                />
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) =>
                    uploadAndSetUrl(
                      event.target.files?.[0] ?? null,
                      folder,
                      (value) => setBalicek((current) => ({ ...current, [field]: value }))
                    )
                  }
                />
              </div>
            ))}

            <button type="button" disabled={busy} style={primaryButtonStyle} onClick={handleSaveBalicek}>
              Uložit balíček
            </button>
          </section>
        ) : null}
      </div>
    </div>
  );
}

export default Admin;
